import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import i18n from "@/i18n";
import { z } from "zod";

// Enum options extracted from Ruby code
export const categoryOptions = [
  { value: "volatility", label: "Volatility" },
  { value: "return_pa", label: "Return Pa" },
  { value: "cumulative_return", label: "Cumulative Return" },
  { value: "max_drawdown", label: "Max Drawdown" },
  { value: "info_ratio", label: "Info Ratio" },
  { value: "calmar_ratio", label: "Calmar Ratio" },
  { value: "perf_month_to_month", label: "Perf Month To Month" },
  { value: "perf_year_to_year", label: "Perf Year To Year" },
  { value: "fixings", label: "Fixings" },
  { value: "correlations", label: "Correlations" },
  { value: "weights", label: "Weights" },
  { value: "real_weights", label: "Real Weights" },
  { value: "churn", label: "Churn" },
  { value: "roe", label: "ROE" },
  { value: "revenue", label: "Revenue" },
  { value: "capi", label: "Capi" },
  { value: "ebitda", label: "Ebitda" },
  { value: "eps", label: "EPS" },
  { value: "volume", label: "Volume" },
  { value: "per", label: "PER" },
  { value: "dividend_amount", label: "Dividend Amount" },
  { value: "fixings_min", label: "Fixings Min" },
  { value: "fixings_max", label: "Fixings Max" },
  { value: "perf_attribution", label: "Perf Attribution" },
  { value: "beta", label: "Beta" },
  { value: "p_value", label: "P Value" },
  { value: "t_stat", label: "T Stat" },
  { value: "rsquared", label: "Rsquared" },
  { value: "timestame", label: "Timestame" },
  { value: "close", label: "Close" },
  { value: "split_coefficient", label: "Split Coefficient" },
  { value: "aum_fund_level", label: "AUM Fund Level" },
  {
    value: "nb_shares_outstanding_share_class",
    label: "Nb Shares Outstanding Share Class",
  },
  { value: "buy_sell", label: "Buy Sell" },
  { value: "aum_share_class", label: "AUM Share Class" },
  { value: "perf_contrib", label: "Perf Contrib" },
  { value: "risk_contrib", label: "Risk Contrib" },
  { value: "rating", label: "Rating" },
  { value: "balance_sheet", label: "Balance Sheet" },
  {
    value: "max_drawdown_drawdown_range",
    label: "Max Drawdown Drawdown Range",
  },
  {
    value: "max_drawdown_recovery_time",
    label: "Max Drawdown Recovery Time",
  },
  { value: "positive_perf_mean", label: "Positive Perf Mean" },
  { value: "positive_perf_nb", label: "Positive Perf Nb" },
  { value: "negative_perf_mean", label: "Negative Perf Mean" },
  { value: "negative_perf_nb", label: "Negative Perf Nb" },
  { value: "sharpe", label: "Sharpe" },
  { value: "sharpe_modified", label: "Sharpe Modified" },
  { value: "sortino", label: "Sortino" },
  { value: "omega", label: "Omega" },
  { value: "normal_bias_ratio", label: "Normal Bias Ratio" },
  { value: "bias_ratio", label: "Bias Ratio" },
  { value: "empirical_var_cutoff", label: "Empirical Var Cutoff" },
  { value: "normal_var", label: "Normal Var" },
  { value: "cornish_fisher_var", label: "Cornish Fisher Var" },
  { value: "empirical_cvar_mean", label: "Empirical CVaR Mean" },
  { value: "upside_vol", label: "Upside Vol" },
  { value: "downside_vol", label: "Downside Vol" },
  { value: "week_to_week_vol", label: "Week To Week Vol" },
  {
    value: "moms_cumulative_return_sample_skewness",
    label: "Moms Cumulative Return Sample Skewness",
  },
  {
    value: "moms_cumulative_return_sample_excess_kurtosis",
    label: "Moms Cumulative Return Sample Excess Kurtosis",
  },
  { value: "perf_quantile_median", label: "Perf Quantile Median" },
  { value: "perf_quantile_min", label: "Perf Quantile Min" },
  { value: "perf_quantile_max", label: "Perf Quantile Max" },
  { value: "raw_fixings", label: "Raw Fixings" },
  { value: "downside_risk", label: "Downside Risk" },
  { value: "bloomberg_sharpe", label: "Bloomberg Sharpe" },
  { value: "cumulative_return_min", label: "Cumulative Return Min" },
  { value: "cumulative_return_max", label: "Cumulative Return Max" },
  { value: "max_drawup", label: "Max Drawup" },
  {
    value: "max_drawup_drawdown_range",
    label: "Max Drawup Drawdown Range",
  },
  { value: "dividend_rate", label: "Dividend Rate" },
  {
    value: "longest_interval_without_values_date",
    label: "Longest Interval Without Values Date",
  },
  {
    value: "longest_interval_without_values_length",
    label: "Longest Interval Without Values Length",
  },
  {
    value: "longest_interval_constant_value_date",
    label: "Longest Interval Constant Value Date",
  },
  {
    value: "longest_interval_constant_value_length",
    label: "Longest Interval Constant Value Length",
  },
  { value: "open_days_ratio", label: "Open Days Ratio" },
  { value: "a3_collateral", label: "A3 Collateral" },
  { value: "n1_fixings", label: "N1 Fixings" },
  { value: "c8_fixings", label: "C8 Fixings" },
  { value: "a2_duration", label: "A2 Duration" },
  { value: "a5_fixings", label: "A5 Fixings" },
  { value: "a6_fixings", label: "A6 Fixings" },
  { value: "l1_fixings", label: "L1 Fixings" },
  { value: "c8_market_value", label: "C8 Market Value" },
  { value: "c8_quantity", label: "C8 Quantity" },
  { value: "score_bgv", label: "Score BGV" },
  { value: "score_dms", label: "Score DMS" },
  { value: "score_edr", label: "Score EDR" },
  { value: "score_evt", label: "Score EVT" },
  { value: "score_pso", label: "Score PSO" },
  { value: "score_global", label: "Score Global" },
  { value: "exclusion_bgv", label: "Exclusion BGV" },
  { value: "exclusion_dms", label: "Exclusion DMS" },
  { value: "exclusion_edr", label: "Exclusion EDR" },
  { value: "exclusion_evt", label: "Exclusion EVT" },
  { value: "exclusion_pso", label: "Exclusion PSO" },
  { value: "exclusion_global", label: "Exclusion Global" },
  { value: "exclusion_quartile", label: "Exclusion Quartile" },
  { value: "eligibility", label: "Eligibility" },
  { value: "sensibility", label: "Sensibility" },
  {
    value: "modified_duration_to_next_option_exercise_date",
    label: "Modified Duration To Next Option Exercise Date",
  },
  {
    value: "modified_duration_to_maturity_date",
    label: "Modified Duration To Maturity Date",
  },
  { value: "yield_to_maturity", label: "Yield To Maturity" },
  { value: "average_coupon", label: "Average Coupon" },
  { value: "barcap_rating", label: "Barcap Rating" },
  { value: "citi_rating", label: "Citi Rating" },
  { value: "moodys_rating", label: "Moodys Rating" },
  { value: "sp_rating", label: "SP Rating" },
  { value: "fitch_rating", label: "Fitch Rating" },
  { value: "barcap_issuer_rating", label: "Barcap Issuer Rating" },
  { value: "citi_issuer_rating", label: "Citi Issuer Rating" },
  { value: "moodys_issuer_rating", label: "Moodys Issuer Rating" },
  { value: "sp_issuer_rating", label: "SP Issuer Rating" },
  { value: "fitch_issuer_rating", label: "Fitch Issuer Rating" },
  { value: "rating_group", label: "Rating Group" },
  { value: "price", label: "Price" },
  { value: "market_value_ptf_ccy", label: "Market Value PTF CCY" },
  { value: "accrued_interests_ptf_ccy", label: "Accrued Interests PTF CCY" },
  {
    value: "market_value_ptf_ccy_without_accrued",
    label: "Market Value PTF CCY Without Accrued",
  },
  { value: "exposition_ptf_ccy", label: "Exposition PTF CCY" },
  { value: "weight_exposition", label: "Weight Exposition" },
  {
    value: "market_value_local_ccy_without_accrued",
    label: "Market Value Local CCY Without Accrued",
  },
  { value: "market_value_local_ccy", label: "Market Value Local CCY" },
  { value: "exposition_local_ccy", label: "Exposition Local CCY" },
  { value: "price_local_ccy", label: "Price Local CCY" },
  { value: "quantity", label: "Quantity" },
  {
    value: "accrued_interests_local_ccy",
    label: "Accrued Interests Local CCY",
  },
  { value: "days_next_coupon", label: "Days Next Coupon" },
  { value: "date_next_coupon", label: "Date Next Coupon" },
  { value: "book_cost_local_ccy", label: "Book Cost Local CCY" },
  { value: "book_cost_ptf_ccy", label: "Book Cost PTF CCY" },
  { value: "unit_book_cost_local_ccy", label: "Unit Book Cost Local CCY" },
  { value: "unit_book_cost_ptf_ccy", label: "Unit Book Cost PTF CCY" },
  { value: "instrument_pricing_date", label: "Instrument Pricing Date" },
  { value: "fx_rate", label: "FX Rate" },
  {
    value: "accrued_interests_cumulated_ptf_ccy",
    label: "Accrued Interests Cumulated PTF CCY",
  },
  { value: "date_next_call", label: "Date Next Call" },
  { value: "quantity_traded", label: "Quantity Traded" },
  { value: "market_value_traded", label: "Market Value Traded" },
  { value: "dividends_ptf_ccy", label: "Dividends PTF CCY" },
  { value: "dividends_local_ccy", label: "Dividends Local CCY" },
  { value: "nb_shares", label: "Nb Shares" },
  { value: "nb_shares_beta", label: "Nb Shares Beta" },
  { value: "price_adjusted", label: "Price Adjusted" },
  { value: "accrued_price_adjusted", label: "Accrued Price Adjusted" },
  {
    value: "price_adjusted_ptf_ccy",
    label: "Price Adjusted PTF CCY",
  },
  {
    value: "accrued_price_adjusted_ptf_ccy",
    label: "Accrued Price Adjusted PTF CCY",
  },
  { value: "excess_return", label: "Excess Return" },
  { value: "months_excess_return", label: "Months Excess Return" },
  { value: "annualized_excess_return", label: "Annualized Excess Return" },
  { value: "tracking_error", label: "Tracking Error" },
  { value: "perf_regression_alpha", label: "Perf Regression Alpha" },
  { value: "perf_regression_beta", label: "Perf Regression Beta" },
  { value: "perf_regression_r2", label: "Perf Regression R2" },
];

export const windowOptions = [
  { value: "start_date", label: "Start Date" },
  { value: "live_date", label: "Live Date" },
  { value: "year_to_date", label: "Year To Date" },
  { value: "3_months", label: "3 Months" },
  { value: "6_months", label: "6 Months" },
  { value: "1_year", label: "1 Year" },
  { value: "2_years", label: "2 Years" },
  { value: "3_years", label: "3 Years" },
  { value: "daily", label: "Daily" },
  { value: "1_week", label: "1 Week" },
  { value: "1_month", label: "1 Month" },
  { value: "5_years", label: "5 Years" },
  { value: "10_years", label: "10 Years" },
  { value: "week_to_week", label: "Week To Week" },
  { value: "month_to_month", label: "Month To Month" },
  { value: "quarter_to_quarter", label: "Quarter To Quarter" },
  { value: "year_to_year", label: "Year To Year" },
  { value: "month_to_date", label: "Month To Date" },
];

//
// Zod schema & type
//
const emptyToUndefined = (v: unknown) =>
  v === "" || v === null ? undefined : v;

export const filterTimeSeriesSchema = z.object({
  owner_id_equals: z
    .preprocess(emptyToUndefined, z.coerce.number().int())
    .optional(),
  owner_type_contains: z.string().optional(),

  category_equals: z.string().nullable().optional(),
  window_equals: z.string().nullable().optional(),
});

export type FilterTimeSeriesSchema = z.infer<typeof filterTimeSeriesSchema>;

export const fields: Field[] = [
  {
    name: "owner_id_equals",
    label: "Owner ID",
    type: "input",
  },
  {
    name: "owner_type_contains",
    label: "Owner Type",
    type: "input",
  },
  {
    name: "category_equals",
    label: "Category",
    type: "select",
    useTranslation: true,
    client: "instit-quantilia",
    table_name: "time_series",
    language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
    translate_key: "category",
    sortByLabel: true,
  },
  {
    name: "window_equals",
    label: "Window",
    type: "select",
    useTranslation: true,
    client: "instit-quantilia",
    table_name: "time_series",
    language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
    translate_key: "window",
    sortByLabel: true,
  },
];

export const client = "qtl%20-%20dynamic%20data";

export function FilterTimeSeries() {
  return (
    <FilterTable
      client={client}
      schema={filterTimeSeriesSchema}
      tableName="time_series"
      customBuildPath={() => `${client}/strategy_time_series`}
      fields={fields}
    />
  );
}
